import { IconButton } from '@mui/material';
import { PickersDay, StaticDatePicker } from '@mui/x-date-pickers';

import { dayjs, type Dayjs } from '@/utils/dayjs';

export interface DayMonthYear {
  day: number;
  month: number;
  year: number;
}

export const DatePicker = ({
  value,
  setValue,
  enableYearSelection = false,
  minDate = dayjs.utc().date(1).month(0),
  maxDate = dayjs.utc().date(1).month(0).add(1, 'year').subtract(1, 'day'),
  isMultiSelect = false,
}: {
  value: DayMonthYear[];
  setValue: (
    value: DayMonthYear[] | ((prevState: DayMonthYear[]) => DayMonthYear[])
  ) => void;
  enableYearSelection?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  isMultiSelect?: boolean;
}) => {
  return (
    <StaticDatePicker
      views={['year', 'month', 'day']}
      minDate={minDate}
      maxDate={maxDate}
      defaultValue={dayjs
        .utc()
        .date(value[0] ? value[0].day : 1)
        .month(value[0] ? value[0].month - 1 : 1)
        .year(value[0] ? value[0].year : dayjs.utc().year())}
      autoFocus={false}
      timezone='UTC'
      slots={{
        nextIconButton: (props) => (
          <IconButton
            {...props}
            disableRipple
            color='primary'
            sx={{
              margin: 0,
            }}
          >
            <i className='material-symbols-chevron-right-rounded' />
          </IconButton>
        ),
        previousIconButton: (props) => (
          <IconButton
            {...props}
            disableRipple
            color='primary'
            sx={{
              margin: 0,
            }}
          >
            <i className='material-symbols-chevron-left-rounded' />
          </IconButton>
        ),
        day: (props) => {
          const day = props.day.date();
          const month = props.day.month() + 1;
          const year = props.day.year();

          const selected = value.some(
            (x) => x.day === day && x.month === month && x.year === year
          );

          return (
            <PickersDay
              {...props}
              autoFocus={false}
              selected={selected}
              onDaySelect={() => {
                if (isMultiSelect) {
                  setValue((prevState: DayMonthYear[]) =>
                    selected
                      ? prevState.filter(
                          (x) =>
                            !(
                              x.day === day &&
                              x.month === month &&
                              x.year === year
                            )
                        )
                      : [...prevState, { day, month, year }]
                  );
                } else {
                  setValue(() => [{ day, month, year }]);
                }
              }}
            />
          );
        },
      }}
      slotProps={{
        switchViewButton: {
          hidden: !enableYearSelection,
        },
        calendarHeader: {
          reduceAnimations: true,
          format: !enableYearSelection ? 'MMMM' : 'MMMM YYYY',
          classes: {
            labelContainer: 'cursor-default',
          },
        },
        actionBar: {
          hidden: true,
        },
        toolbar: {
          hidden: true,
        },
      }}
      sx={{
        minWidth: '280px',

        '& .MuiDateCalendar-root': {
          width: '280px',
          height: 'auto',
          maxHeight: '400px',
          margin: '0 12px',
        },

        '& .MuiPickersSlideTransition-root': {
          minHeight: '256px',
        },

        '& .MuiPickersCalendarHeader-root': {
          padding: '0 8px',
        },

        '& .MuiPickersCalendarHeader-label': {
          fontSize: '13px',
          fontWeight: 500,
        },

        '& .MuiPickersDay-root': {
          minWidth: '24px',
          minHeight: '24px',
          width: '24px',
          height: '24px',
          margin: '8px',
          fontWeight: 400,

          '&:hover': {
            backgroundColor: 'var(--mui-palette-primary-dark)',
          },

          '&.Mui-selected:hover': {
            backgroundColor: 'var(--mui-palette-primary-main)',
          },
        },

        '& .MuiMonthCalendar-root': {
          width: '100%',
        },

        '& .MuiYearCalendar-root': {
          width: '100%',
        },

        '& .Mui-disabled': {
          color: 'var(--mui-palette-text-disabled) !important',
        },

        '& .MuiMonthCalendar-button': {
          '&:hover': {
            backgroundColor: 'var(--mui-palette-primary-dark)',
          },

          '&.Mui-selected': {
            backgroundColor: 'var(--mui-palette-primary-main)',
          },
        },

        '& .MuiYearCalendar-button': {
          '&:hover': {
            backgroundColor: 'var(--mui-palette-primary-dark)',
          },

          '&.Mui-selected': {
            backgroundColor: 'var(--mui-palette-primary-main)',
          },
        },
      }}
    />
  );
};
